/**
 * VP-329: kto rozlicza użytkownika — jedno źródło prawdy dla backendu i frontu.
 *
 * Shopify wymaga (App Store §1.2.1), żeby merchant, który zainstalował apkę ze
 * sklepu, płacił przez Shopify App Pricing, a nie przez nasz checkout. Nie da się
 * tego rozstrzygnąć po „ma sklep Shopify" — klient z viral.partners też może
 * podpiąć sklep i zostaje na Stripe. Dlatego user ma jawną kolumnę
 * `users.billing_provider`, ustawianą przy claimie instalacji z App Store.
 *
 * Wartości:
 *  - `stripe`  — domyślne; checkout, portal, token packi, trial jak dotąd,
 *  - `shopify` — plan wybrany na stronie planów Shopify; cały Stripe niedostępny,
 *  - `manual`  — konto aktywowane ręcznie (demo, wewnętrzne, umowy indywidualne):
 *                ma dostęp, ale nie pokazujemy mu siatki planów Stripe. To właśnie
 *                ta ścieżka (`subscription_active=true` bez `stripe_customer_id`)
 *                wpuściła recenzenta Shopify na checkout.stripe.com.
 */

export type BillingProvider = "stripe" | "shopify" | "manual";

export const BILLING_PROVIDERS: readonly BillingProvider[] = ["stripe", "shopify", "manual"];

export const DEFAULT_BILLING_PROVIDER: BillingProvider = "stripe";

export function isBillingProvider(value: unknown): value is BillingProvider {
  return typeof value === "string" && BILLING_PROVIDERS.includes(value as BillingProvider);
}

/**
 * Normalizuje wartość z DB / API. Nierozpoznane i puste → `stripe`, bo to
 * zachowanie sprzed VP-329: konto bez jawnego providera płaci przez Stripe.
 */
export function normalizeBillingProvider(value: unknown): BillingProvider {
  return isBillingProvider(value) ? value : DEFAULT_BILLING_PROVIDER;
}

/** Czy pokazywać/obsługiwać cokolwiek ze Stripe (checkout, portal, token packi, trial). */
export function usesStripeBilling(value: unknown): boolean {
  return normalizeBillingProvider(value) === "stripe";
}

/** Czy plan pochodzi z Shopify App Pricing (zarządzany po stronie Shopify). */
export function usesShopifyBilling(value: unknown): boolean {
  return normalizeBillingProvider(value) === "shopify";
}
